import { isBoolean, upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';
import { paths } from '@common/types';
import { stableKey } from '@common/cache/util.ts';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const rolesRequest = async () => {
  const response =
    axiosInstance.get<
      paths[`/user/roles`]['get']['responses']['200']['content']['application/json']
    >(`/user/roles`);
  const data = (await response).data;

  return data;
};

export const useRolesQuery = () => {
  const query = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesRequest(),
  });

  return query;
};

const titlesRequest = async () => {
  const response =
    axiosInstance.get<
      paths[`/user/titles`]['get']['responses']['200']['content']['application/json']
    >(`/user/titles`);
  const data = (await response).data;

  return data;
};

export const useTilesQuery = () => {
  const query = useQuery({
    queryKey: ['titles'],
    queryFn: () => titlesRequest(),
  });

  return query;
};

interface IUserListProps {
  search: string;
  page: number;
  limit: number;
  sort_order?: string;
  sort_by?: string;
  status?: string;
  role?: string;
  assigned_entity_id?: string;
  team_id?: string;
  supervisor_id?: string;
}

const userListRequest = async (props: IUserListProps) => {
  const response = axiosInstance.get<
    paths[`/record/users`]['get']['responses']['200']['content']['application/json']
  >(`/record/users`, {
    params: {
      page: props.page,
      limit: props.limit,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.search !== '' && { search: props.search }),
      ...(props.status !== '' && { status: props.status }),
      ...(props.role !== '' && { role: props.role }),
      ...(props.team_id !== '' && { team_id: props.team_id }),
      ...(props.assigned_entity_id !== '' && {
        assigned_entity_id: props.assigned_entity_id,
      }),
      ...(props.supervisor_id !== '' && { supervisor_id: props.supervisor_id }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useUserListQuery = (props: IUserListProps) => {
  const query = useQuery({
    queryKey: ['users', stableKey(props)],
    queryFn: () => userListRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

type IUseUserListShortQueryProps =
  paths[`/record/users/short`]['get']['parameters']['query'];

const userListShort = async (props: IUseUserListShortQueryProps) => {
  const response = axiosInstance.get<
    paths[`/record/users/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/users/short`, { params: props });
  const data = (await response).data;

  return data;
};

export const useUserListShortQuery = (
  props: IUseUserListShortQueryProps & { disabled?: boolean },
) => {
  const orgQuery = useQuery({
    enabled: !props.disabled,
    queryKey: ['userListShort', stableKey(props)],
    queryFn: () => userListShort(props),
  });

  return orgQuery;
};

interface IUserProps {
  user_id: string;
}

const userRequest = async (props: IUserProps) => {
  const response = axiosInstance.get<
    paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json']
  >(`/user/${props.user_id}`);
  const data = (await response).data;

  return data;
};

export const useUserQuery = (props: IUserProps) => {
  const query = useQuery({
    queryKey: ['user', props.user_id],
    queryFn: () => userRequest(props),
  });

  return query;
};

interface IUserHistoryProps {
  page: number;
  limit: number;
  user_id: string;
  enabled: boolean;
}

const userHistoryRequest = async (props: IUserHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/user/{user_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/user/${props.user_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useUserHistoryQuery = (props: IUserHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['user', 'history', props.user_id, props.page],
    queryFn: () => userHistoryRequest(props),
  });

  return query;
};

type IActivityLogsProps = paths[`/activities`]['get']['parameters']['query'];

const activityLogsRequest = async (props: IActivityLogsProps) => {
  const response = axiosInstance.get<
    paths[`/activities`]['get']['responses']['200']['content']['application/json']
  >(`/activities`, { params: props });
  const data = (await response).data;

  return data;
};

export const useActivityLogsQuery = (props: IActivityLogsProps) => {
  const query = useQuery({
    queryKey: ['activityLog', 'list', stableKey(props)],
    queryFn: () => activityLogsRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

const activityLogRequest = async (activity_id: string) => {
  const response = axiosInstance.get<
    paths[`/activities/{activity_id}`]['get']['responses']['200']['content']['application/json']
  >(`/activities/${activity_id}`);
  const data = (await response).data;

  return data;
};

export const useActivityLogQuery = (
  activity_id: string,
  enabled: boolean = true,
) => {
  const query = useQuery({
    //this is done in order to prevent unnecessary fetches
    enabled: !!activity_id && enabled,
    queryKey: ['activityLog', activity_id],
    queryFn: () => activityLogRequest(activity_id),
  });

  return query;
};

const activityLogMetricsRequest = async () => {
  const response = axiosInstance.get<
    paths[`/activities/metrics`]['get']['responses']['200']['content']['application/json']
  >(`/activities/metrics`, {
    params: {
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const useActivityLogMetricsQuery = () => {
  const query = useQuery({
    queryKey: ['activityLog', 'metrics'],
    queryFn: () => activityLogMetricsRequest(),
  });

  return query;
};

type IUseTimeOffUserListShortQueryProps =
  paths[`/user-time-off/direct-reports`]['get']['parameters']['query'];

const timeOffUserListShort = async (
  props: IUseTimeOffUserListShortQueryProps,
) => {
  const response = axiosInstance.get<
    paths[`/user-time-off/direct-reports`]['get']['responses']['200']['content']['application/json']
  >(`/user-time-off/direct-reports`, { params: props });
  const data = (await response).data;

  return data;
};

export const useTimeOffUserListShortQuery = (
  props: IUseTimeOffUserListShortQueryProps & { disabled?: boolean },
) => {
  const orgQuery = useQuery({
    // enabled: !props.disabled,
    queryKey: ['timeOffShort', stableKey(props)],
    queryFn: () => timeOffUserListShort(props),
  });

  return orgQuery;
};

type ITimeOffListProps = Partial<
  paths[`/user-time-off`]['get']['parameters']['query']
>;

const timeOffListRequest = async (props: ITimeOffListProps) => {
  const response = axiosInstance.get<
    paths[`/user-time-off`]['get']['responses']['200']['content']['application/json']
  >(`/user-time-off`, {
    params: {
      page: props.page,
      limit: props.limit,
      ...(props.start_date && { start_date: props.start_date }),
      ...(props.end_date && { end_date: props.end_date }),
      ...(isBoolean(props.only_upcoming) && {
        only_upcoming: props.only_upcoming,
      }),
      ...(props.user_ids && { user_ids: props.user_ids }),
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const useTimeOffListQuery = (props: ITimeOffListProps) => {
  const query = useQuery({
    queryKey: ['timeOff', 'list', stableKey(props)],
    queryFn: () => timeOffListRequest(props),
  });

  return query;
};

const timeOffRequest = async (time_off_id: string) => {
  const response = axiosInstance.get<
    paths[`/user-time-off/{time_off_id}`]['get']['responses']['200']['content']['application/json']
  >(`/user-time-off/${time_off_id}`);
  const data = (await response).data;

  return data;
};

export const useTimeOffQuery = (
  time_off_id: string,
  enabled: boolean = true,
) => {
  const query = useQuery({
    enabled: !!time_off_id && enabled,
    queryKey: ['timeOff', time_off_id],
    queryFn: () => timeOffRequest(time_off_id),
  });

  return query;
};
